/**
 * OpenELP Game Engine v3
 * 
 * The core game system that turns learning into an adventure:
 * - XP & Level progression (Explorer ranks)
 * - Energy system (lives for lessons)
 * - Combo multiplier (consecutive correct = more XP)
 * - Achievement auto-trigger system
 * - Unit unlock logic (gate progression)
 * - Daily quests with bonus rewards
 * - Story chapters (narrative progression)
 * - Power-ups (hint, skip, freeze, double-XP)
 * - Word gem collection (vocabulary mastery)
 * - Mastery streaks & skill constellations
 * - Metacognitive checkpoint rewards
 * - Adaptive difficulty integration
 */

import type { PersonalizationState } from './personalization'
import { createInitialPersonalizationState } from './personalization'

// ========================
// Types
// ========================

export interface GameState {
  xp: number
  level: number
  energy: number
  maxEnergy: number
  combo: number
  maxCombo: number
  comboMultiplier: number
  wordGems: string[]
  powerUps: PowerUpInventory
  achievements: string[]
  dailyQuest: DailyQuest | null
  lastEnergyRefill: number
  storyChapter: number
  completedLessons: string[]
  unlockedUnits: string[]
  bossDefeated: string[]
  /** Personalization engine state — the brain of adaptive learning */
  personalization: PersonalizationState
  /** Current mastery streak (consecutive mastered words) */
  masteryStreak: number
  /** Total words mastered */
  totalMastered: number
  /** Skill constellation — unlocked skill stars */
  skillStars: string[]
  /** Metacognitive checkpoints completed */
  checkpointsPassed: number
}

export interface PowerUpInventory {
  hint: number
  skip: number
  freeze: number
  doubleXp: number
}

export type PowerUpType = 'hint' | 'skip' | 'freeze' | 'doubleXp'

export interface DailyQuest {
  date: string
  goal: number
  progress: number
  reward: number
  type: 'words' | 'lessons' | 'perfect' | 'combo'
  title: string
  completed: boolean
}

export interface Achievement {
  id: string
  title: string
  description: string
  icon: string
  condition: (state: GameState, session: SessionStats) => boolean
  reward: number
}

export interface SessionStats {
  correctCount: number
  totalCount: number
  maxCombo: number
  wordsLearned: number
  perfectScenes: number
  layerChanges: number
  startTime: number
  /** Categories practiced this session */
  categoriesPracticed: string[]
  /** Words mastered this session */
  wordsMastered: string[]
  /** Fastest correct answer in ms */
  fastestCorrect: number
  /** Metacognitive reflections completed */
  reflectionsCompleted: number
}

export interface XPEvent {
  amount: number
  reason: string
  combo: number
  multiplier: number
  final: number
}

// ========================
// Level / Rank System
// ========================

export interface ExplorerRank {
  level: number
  title: string
  minXp: number
  emoji: string
  color: string
}

const ranks: ExplorerRank[] = [
  { level: 1,  title: '新芽探险家',   minXp: 0,     emoji: '🌱', color: '#84cc16' },
  { level: 2,  title: '追光探险家',   minXp: 100,   emoji: '⭐', color: '#eab308' },
  { level: 3,  title: '寻宝探险家',   minXp: 300,   emoji: '🔍', color: '#06b6d4' },
  { level: 4,  title: '风暴探险家',   minXp: 600,   emoji: '⚡', color: '#6366f1' },
  { level: 5,  title: '星辰探险家',   minXp: 1000,  emoji: '🌟', color: '#a855f7' },
  { level: 6,  title: '传奇探险家',   minXp: 1800,  emoji: '🏆', color: '#ec4899' },
  { level: 7,  title: '神话探险家',   minXp: 3000,  emoji: '👑', color: '#f43f5e' },
]

export function getRank(xp: number): ExplorerRank {
  let current = ranks[0]
  for (const r of ranks) {
    if (xp >= r.minXp) current = r
  }
  return current
}

export function getNextRank(xp: number): ExplorerRank | null {
  const current = getRank(xp)
  const idx = ranks.findIndex(r => r.level === current.level)
  return idx < ranks.length - 1 ? ranks[idx + 1] : null
}

export function getLevelProgress(xp: number): { current: number; needed: number; percent: number } {
  const current = getRank(xp)
  const next = getNextRank(xp)
  if (!next) return { current: xp, needed: xp, percent: 100 }
  const earned = xp - current.minXp
  const total = next.minXp - current.minXp
  return { current: earned, needed: total, percent: Math.min(100, Math.round((earned / total) * 100)) }
}

// ========================
// XP Calculation
// ========================

const BASE_XP = {
  correctAnswer: 10,
  perfectScene: 5,
  wordLearned: 8,
  sentenceBuilt: 12,
  spellingCorrect: 10,
  listeningCorrect: 10,
  speakingAttempt: 15,
  quizCorrect: 15,
  wordMatched: 6,
  lessonComplete: 50,
  bossDefeated: 100,
  // v3 Personalization XP
  wordMastered: 30,
  masteryStreak: 25,
  checkpointPassed: 40,
  reflectionCompleted: 20,
  weaknessOvercome: 35,
  reviewStreak: 15,
}

export function calculateXP(
  action: keyof typeof BASE_XP,
  combo: number,
  doubleXpActive: boolean
): XPEvent {
  const base = BASE_XP[action]
  const multiplier = getComboMultiplier(combo)
  let final = Math.round(base * multiplier)
  if (doubleXpActive) final *= 2
  return { amount: base, reason: action, combo, multiplier, final }
}

export function getComboMultiplier(combo: number): number {
  if (combo >= 10) return 3.0
  if (combo >= 7) return 2.5
  if (combo >= 5) return 2.0
  if (combo >= 3) return 1.5
  if (combo >= 1) return 1.2
  return 1.0
}

// ========================
// Energy System
// ========================

const MAX_ENERGY = 5
const ENERGY_REFILL_MINUTES = 30

export function getEnergyInfo(state: GameState): { current: number; max: number; nextRefillMs: number } {
  const now = Date.now()
  const elapsed = now - state.lastEnergyRefill
  const refills = Math.floor(elapsed / (ENERGY_REFILL_MINUTES * 60 * 1000))
  const current = Math.min(state.maxEnergy, state.energy + refills)
  const nextRefillMs = (ENERGY_REFILL_MINUTES * 60 * 1000) - (elapsed % (ENERGY_REFILL_MINUTES * 60 * 1000))
  return { current, max: state.maxEnergy, nextRefillMs }
}

export function shouldRefillEnergy(state: GameState): boolean {
  if (state.energy >= state.maxEnergy) return false
  const now = Date.now()
  const elapsed = now - state.lastEnergyRefill
  return elapsed >= ENERGY_REFILL_MINUTES * 60 * 1000
}

export function refillEnergy(state: GameState): GameState {
  if (!shouldRefillEnergy(state)) return state
  const now = Date.now()
  const elapsed = now - state.lastEnergyRefill
  const refills = Math.floor(elapsed / (ENERGY_REFILL_MINUTES * 60 * 1000))
  const newEnergy = Math.min(state.maxEnergy, state.energy + refills)
  const consumedRefillTime = refills * ENERGY_REFILL_MINUTES * 60 * 1000
  return {
    ...state,
    energy: newEnergy,
    lastEnergyRefill: state.lastEnergyRefill + consumedRefillTime,
  }
}

// ========================
// Combo System
// ========================

export function updateCombo(currentCombo: number, correct: boolean): number {
  if (correct) return currentCombo + 1
  return 0
}

export function getComboLabel(combo: number): string {
  if (combo >= 10) return 'INFERNO! 🔥🔥🔥'
  if (combo >= 7) return 'BLAZING! 🔥🔥'
  if (combo >= 5) return 'ON FIRE! 🔥'
  if (combo >= 3) return 'COMBO! ⚡'
  if (combo >= 1) return 'Nice! ✨'
  return ''
}

// ========================
// Power-Ups
// ========================

export const powerUpInfo: Record<PowerUpType, { name: string; emoji: string; desc: string; color: string }> = {
  hint:   { name: '提示宝石', emoji: '💡', desc: '在一个题目中获得提示', color: '#fbbf24' },
  skip:   { name: '跳过卷轴', emoji: '⏭️', desc: '跳过一个题目不扣能量', color: '#06b6d4' },
  freeze: { name: '时间沙漏', emoji: '⏳', desc: '冻结计时器10秒', color: '#a855f7' },
  doubleXp: { name: '双倍卷轴', emoji: '✨', desc: '下一次答题获得双倍XP', color: '#ec4899' },
}

export function usePowerUp(state: GameState, type: PowerUpType): GameState {
  if (state.powerUps[type] <= 0) return state
  return {
    ...state,
    powerUps: {
      ...state.powerUps,
      [type]: state.powerUps[type] - 1,
    },
  }
}

export function awardPowerUp(state: GameState, type: PowerUpType, amount = 1): GameState {
  return {
    ...state,
    powerUps: {
      ...state.powerUps,
      [type]: state.powerUps[type] + amount,
    },
  }
}

// ========================
// Achievements
// ========================

export const achievements: Achievement[] = [
  { id: 'first-word',     title: '第一个单词',     description: '学会第一个单词',           icon: '📚', reward: 20,  condition: (g, s) => g.wordGems.length >= 1 || s.wordsLearned >= 1 },
  { id: 'word-collector', title: '词汇收藏家',   description: '学会10个单词',             icon: '💎', reward: 50,  condition: (g, s) => g.wordGems.length >= 10 || s.wordsLearned >= 10 },
  { id: 'combo-5',        title: '连击大师',       description: '达成5连击',                icon: '⚡', reward: 30,  condition: (g, s) => Math.max(g.maxCombo, s.maxCombo) >= 5 },
  { id: 'combo-10',       title: '完美风暴',       description: '达成10连击',               icon: '🔥', reward: 80,  condition: (g, s) => Math.max(g.maxCombo, s.maxCombo) >= 10 },
  { id: 'perfect-lesson', title: '满分课堂',       description: '一节课全对',               icon: '🌟', reward: 100, condition: (_g, s) => s.totalCount > 0 && s.correctCount === s.totalCount },
  { id: 'first-boss',     title: '初代屠龙者',     description: '击败第一个Boss',           icon: '⚔️', reward: 150, condition: (_g, s) => s.totalCount >= 10 && s.correctCount / s.totalCount >= 0.8 },
  { id: 'speed-runner',   title: '闪电侠',         description: '5分钟内完成一节课',        icon: '💨', reward: 60,  condition: (_g, s) => Date.now() - s.startTime < 5 * 60 * 1000 && s.totalCount >= 5 },
  // v3 Personalization achievements
  { id: 'first-mastery',  title: '初窥门径',       description: '精通第一个单词（掌握度≥85%）', icon: '👑', reward: 40,  condition: (g) => g.totalMastered >= 1 },
  { id: 'word-master-10', title: '词汇大师',       description: '精通10个单词',             icon: '🏅', reward: 100, condition: (g) => g.totalMastered >= 10 },
  { id: 'mastery-streak', title: '精通连击',       description: '连续精通3个单词',          icon: '💫', reward: 80,  condition: (g) => g.masteryStreak >= 3 },
  { id: 'self-aware',     title: '元认知觉醒',     description: '完成第一次自我反思',       icon: '🧠', reward: 50,  condition: (g) => g.checkpointsPassed >= 1 },
  { id: 'constellation',  title: '星座点亮',       description: '点亮第一个技能星座',       icon: '✨', reward: 60,  condition: (g) => g.skillStars.length >= 1 },
  { id: 'comeback-kid',   title: '逆袭王者',       description: '从降层后重新升层',         icon: '🔄', reward: 70,  condition: (_g, s) => s.layerChanges >= 2 },
]

export function checkAchievements(state: GameState, session: SessionStats): Achievement[] {
  return achievements.filter(a => 
    !state.achievements.includes(a.id) && a.condition(state, session)
  )
}

// ========================
// Unit Unlock System
// ========================

export function isUnitUnlocked(unitId: string, state: GameState): boolean {
  // First unit is always unlocked
  if (state.unlockedUnits.includes(unitId)) return true
  return false
}

export function unlockNextUnit(currentUnitId: string, state: GameState): GameState {
  if (state.unlockedUnits.includes(currentUnitId)) return state
  return {
    ...state,
    unlockedUnits: [...state.unlockedUnits, currentUnitId],
  }
}

// ========================
// Daily Quest
// ========================

const questTemplates = [
  { type: 'words' as const,   goal: 5,  reward: 30,  title: '学会5个新单词' },
  { type: 'lessons' as const, goal: 1,  reward: 50,  title: '完成1节AI课堂' },
  { type: 'perfect' as const, goal: 3,  reward: 40,  title: '3个完美答题' },
  { type: 'combo' as const,  goal: 5,  reward: 50,  title: '达成5连击' },
]

export function generateDailyQuest(): DailyQuest {
  const today = new Date().toISOString().split('T')[0]
  const seed = today.split('-').join('').split('').reduce((a, b) => a + parseInt(b), 0)
  const template = questTemplates[seed % questTemplates.length]
  return {
    date: today,
    goal: template.goal,
    progress: 0,
    reward: template.reward,
    type: template.type,
    title: template.title,
    completed: false,
  }
}

export function isQuestExpired(quest: DailyQuest): boolean {
  const today = new Date().toISOString().split('T')[0]
  return quest.date !== today
}

export function updateQuestProgress(quest: DailyQuest, type: string, amount = 1): DailyQuest {
  if (quest.completed) return quest
  if (quest.type !== type) return quest
  const newProgress = Math.min(quest.goal, quest.progress + amount)
  return {
    ...quest,
    progress: newProgress,
    completed: newProgress >= quest.goal,
  }
}

// ========================
// Story System
// ========================

export interface StoryBeat {
  chapter: number
  title: string
  intro: string
  outro: string
  emoji: string
}

export const storyChapters: StoryBeat[] = [
  {
    chapter: 1,
    title: '彩虹岛的问候',
    intro: '欢迎来到英语彩虹岛！你是一名新晋探险家，岛上的居民说英语。Ms. Lily老师将带你认识新朋友，学会打招呼，开启你的冒险之旅！',
    outro: '太棒了！你已经学会了基本的问候。岛上的居民开始认识你了。下一站，让我们去探索文具森林吧！',
    emoji: '🌈',
  },
  {
    chapter: 2,
    title: '文具森林的秘密',
    intro: '在文具森林深处，传说藏着能让人过目不忘的"记忆水晶"。但要找到它，你必须先学会用英语说出各种文具的名字！',
    outro: '你找到了记忆水晶！从此以后，新学的单词会记得更牢。冒险继续！',
    emoji: '🎒',
  },
  {
    chapter: 3,
    title: '动物王国的大挑战',
    intro: '动物王国的国王邀请你参加一年一度的英语挑战赛。只有答对所有问题，才能赢得"动物之友"勋章！',
    outro: '国王亲自为你颁发了勋章！动物们欢呼着送你前往下一站。',
    emoji: '🦁',
  },
  {
    chapter: 4,
    title: '水果谷的丰收节',
    intro: '水果谷正在举办丰收节！每个摊位都需要用英语说出水果名字才能品尝。收集所有水果徽章，解锁神秘宝藏！',
    outro: '你品尝了所有水果，获得了"丰收之星"称号！宝藏的线索指向了远处的高山...',
    emoji: '🍎',
  },
  {
    chapter: 5,
    title: '时间塔的终极考验',
    intro: '终于来到了传说中的时间塔！塔顶的守护者会用最难的问题考验你。准备好你学过的所有知识，这是最终决战！',
    outro: '你征服了时间塔！从塔顶俯瞰整个英语彩虹岛，所有你走过的地方都在闪耀着光芒。你已经成为了一名真正的传奇探险家！',
    emoji: '⏰',
  },
]

export function getStoryChapter(unitNum: number): StoryBeat {
  const idx = Math.min(unitNum - 1, storyChapters.length - 1)
  return storyChapters[idx]
}

// ========================
// Initial State
// ========================

export function createInitialGameState(): GameState {
  return {
    xp: 0,
    level: 1,
    energy: MAX_ENERGY,
    maxEnergy: MAX_ENERGY,
    combo: 0,
    maxCombo: 0,
    wordGems: [],
    powerUps: { hint: 1, skip: 1, freeze: 0, doubleXp: 1 },
    achievements: [],
    dailyQuest: generateDailyQuest(),
    lastEnergyRefill: Date.now(),
    storyChapter: 1,
    completedLessons: [],
    unlockedUnits: [],
    bossDefeated: [],
    personalization: createInitialPersonalizationState(),
    masteryStreak: 0,
    totalMastered: 0,
    skillStars: [],
    checkpointsPassed: 0,
    comboMultiplier: 1.0,
  }
}

// ========================
// Session Helpers
// ========================

export function createSessionStats(): SessionStats {
  return {
    correctCount: 0,
    totalCount: 0,
    maxCombo: 0,
    wordsLearned: 0,
    perfectScenes: 0,
    layerChanges: 0,
    startTime: Date.now(),
    categoriesPracticed: [],
    wordsMastered: [],
    fastestCorrect: 0,
    reflectionsCompleted: 0,
  }
}

export function awardXP(state: GameState, event: XPEvent): { state: GameState; leveledUp: boolean; newRank: ExplorerRank | null } {
  const oldRank = getRank(state.xp)
  const newXp = state.xp + event.final
  const newRank = getRank(newXp)
  const leveledUp = newRank.level > oldRank.level
  return {
    state: { ...state, xp: newXp, level: newRank.level },
    leveledUp,
    newRank: leveledUp ? newRank : null,
  }
}

// ========================
// Mastery Tracking & Skill Constellations
// ========================

/**
 * Check if a word just reached mastery and update game state accordingly.
 */
export function checkMasteryAchieved(
  state: GameState,
  word: string,
  newMastery: number,
  oldMastery: number
): { state: GameState; mastered: boolean; xpEvent: XPEvent | null } {
  if (newMastery >= 85 && oldMastery < 85) {
    // Word just mastered!
    const newStreak = state.masteryStreak + 1
    const newTotalMastered = state.totalMastered + 1
    let xpEvent: XPEvent | null = null

    // Award mastery XP
    xpEvent = calculateXP('wordMastered', state.combo, false)

    // Bonus XP for mastery streaks
    if (newStreak >= 3) {
      const streakEvent = calculateXP('masteryStreak', state.combo, false)
      xpEvent = { ...xpEvent, final: xpEvent.final + streakEvent.final }
    }

    return {
      state: {
        ...state,
        masteryStreak: newStreak,
        totalMastered: newTotalMastered,
        wordGems: state.wordGems.includes(word) ? state.wordGems : [...state.wordGems, word],
      },
      mastered: true,
      xpEvent,
    }
  }

  // Reset mastery streak if mastery drops
  if (newMastery < 85 && oldMastery >= 85) {
    return {
      state: { ...state, masteryStreak: 0 },
      mastered: false,
      xpEvent: null,
    }
  }

  return { state, mastered: false, xpEvent: null }
}

/**
 * Award a skill constellation star when a category reaches consistent proficiency.
 */
export function awardSkillStar(state: GameState, category: string): GameState {
  if (state.skillStars.includes(category)) return state
  return {
    ...state,
    skillStars: [...state.skillStars, category],
    checkpointsPassed: state.checkpointsPassed + 1,
  }
}

/**
 * Record a metacognitive checkpoint completion.
 */
export function recordCheckpoint(state: GameState): GameState {
  return {
    ...state,
    checkpointsPassed: state.checkpointsPassed + 1,
  }
}
